<template>
  <v-dialog v-model="internalDialog" max-width="1100px" scrollable persistent transition="dialog-bottom-transition">
    <v-card class="rounded-lg overflow-hidden bg-white">
      <!-- HEADER -->
      <v-card-title class="d-flex justify-space-between align-center py-4 px-6 info darken-2 white--text">
        <div class="d-flex align-center">
          <v-avatar color="white" size="40" class="me-3">
            <v-icon color="info darken-2" size="24">mdi-account-cash</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-black white--text mb-0">تقرير أرباح السائقين التفصيلي</div>
            <div class="text-caption white--text text--lighten-3 opacity-90">
              مستحقات وأرباح السائقين المحسوبة من حركات المحفظة الفعلية وتفاصيل رحلات كل سائق
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-btn
            color="white"
            light
            class="font-weight-bold elevation-2 me-3 text-none info--text text--darken-3"
            :loading="loadingCsv"
            @click="exportCsv"
          >
            <v-icon left color="info darken-3">mdi-file-delimited</v-icon>
            تصدير CSV
          </v-btn>

          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- BODY -->
      <v-card-text class="pa-5 bg-grey-lighten-5">
        <v-alert v-if="errorMessage" type="error" dense class="mb-4 font-weight-bold d-flex align-center justify-space-between">
          <div>{{ errorMessage }}</div>
          <v-btn x-small color="white" text class="font-weight-bold ms-3" @click="fetchReport">إعادة المحاولة</v-btn>
        </v-alert>

        <!-- 1. FILTERS BAR -->
        <v-card outlined class="pa-4 mb-5 rounded-lg border-grey bg-white elevation-1">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">الفترة الزمنية:</div>
              <v-select
                v-model="selectedPeriod"
                :items="periodOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-select>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">من تاريخ:</div>
              <v-text-field
                v-model="dateFrom"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">إلى تاريخ:</div>
              <v-text-field
                v-model="dateTo"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">بحث باسم السائق أو الهاتف:</div>
              <v-text-field
                v-model="searchQuery"
                placeholder="ابحث..."
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="bg-white"
                @keyup.enter="fetchReport"
                @click:clear="onClearSearch"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card>

        <!-- 2. KPI SUMMARY CARDS -->
        <v-row dense class="mb-5">
          <v-col cols="12" sm="4">
            <v-card outlined class="pa-4 rounded-lg border-info bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي أرباح السائقين المستحقة</div>
              <div class="text-h5 font-weight-black info--text text--darken-3 mt-1">
                {{ summary.total_driver_earnings || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">مستحقات الرحلات المكتملة</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="4">
            <v-card outlined class="pa-4 rounded-lg border-primary bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">عدد السائقين النشطين</div>
              <div class="text-h5 font-weight-black primary--text mt-1">
                {{ summary.drivers_count || 0 }} <span class="text-caption font-weight-bold">سائق</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">السائقون الذين لديهم رحلات/أرباح</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="4">
            <v-card outlined class="pa-4 rounded-lg border-success bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي الرحلات المشغلة</div>
              <div class="text-h5 font-weight-black green--text text--darken-3 mt-1">
                {{ summary.total_trips_operated || 0 }} <span class="text-caption font-weight-bold">رحلة</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">رحلات مجدولة ومنفذة خلال الفترة</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- 3. DRIVERS TABLE WITH EXPANDABLE DRILLDOWN -->
        <v-card outlined class="rounded-lg border-grey bg-white elevation-1">
          <v-card-title class="pa-3 grey lighten-4 border-bottom d-flex align-center justify-space-between">
            <span class="text-subtitle-1 font-weight-bold text-dark-primary">
              <v-icon size="20" color="info" class="me-1">mdi-account-group</v-icon>
              قائمة السائقين وتفاصيل الرحلات (انقر لعرض رحلات السائق)
            </span>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="table.data"
            :loading="loading"
            show-expand
            single-expand
            :expanded.sync="expanded"
            item-key="driver_id"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.total_earnings="{ item }">
              <span class="font-weight-black info--text text--darken-3">{{ item.total_earnings }} EGP</span>
            </template>

            <!-- Expanded Details Row -->
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-4 bg-grey-lighten-4">
                <v-card outlined class="pa-3 rounded-lg border-info bg-white">
                  <div class="font-weight-bold text-subtitle-2 info--text text--darken-3 mb-2">
                    تفاصيل أرباح رحلات السائق: {{ item.driver_name }} ({{ item.trip_breakdown ? item.trip_breakdown.length : 0 }} رحلة)
                  </div>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>رقم الرحلة المجدولة</th>
                          <th>المسار</th>
                          <th>تاريخ الحركة</th>
                          <th>أرباح السائق من الرحلة</th>
                          <th>الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="b in item.trip_breakdown" :key="b.transaction_id">
                          <td class="font-weight-bold">#{{ b.planned_trip_id }}</td>
                          <td>{{ b.route_name }}</td>
                          <td>{{ b.date }}</td>
                          <td class="font-weight-bold info--text text--darken-3">{{ b.driver_earning }} EGP</td>
                          <td>
                            <v-chip x-small color="success" class="white--text font-weight-bold">{{ b.status }}</v-chip>
                          </td>
                        </tr>
                        <tr v-if="!item.trip_breakdown || item.trip_breakdown.length === 0">
                          <td colspan="5" class="text-center text-dark-secondary py-3">لا توجد حركات أرباح منفذة لهذا السائق</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card>
              </td>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-6 text-dark-secondary">لا توجد بيانات سائقين مطابقة</div>
            </template>
          </v-data-table>

          <!-- PAGINATION -->
          <div class="pa-3 border-top d-flex justify-space-between align-center flex-wrap gap-2">
            <div class="text-caption text-dark-secondary font-weight-bold">
              عرض الصفحة {{ table.page }} من {{ table.last_page }} (إجمالي {{ table.total }} سائق)
            </div>
            <v-pagination
              v-if="table.last_page > 1"
              v-model="table.page"
              :length="table.last_page"
              total-visible="7"
              dense
              @input="fetchReport"
            ></v-pagination>
          </div>
        </v-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 bg-white d-flex justify-space-between align-center">
        <div class="caption font-weight-bold text-dark-secondary">
          BEEPGO Reporting Center — تقرير أرباح السائقين
        </div>
        <v-btn color="grey darken-2" text class="font-weight-bold" @click="closeModal">إغلاق</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'DriverEarningsModal',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      internalDialog: false,
      loading: false,
      loadingCsv: false,
      errorMessage: null,

      selectedPeriod: 'month',
      dateFrom: null,
      dateTo: null,
      searchQuery: '',

      periodOptions: [
        { text: 'اليوم (Today)', value: 'today' },
        { text: 'هذا الأسبوع (This Week)', value: 'week' },
        { text: 'هذا الشهر (This Month)', value: 'month' },
        { text: 'هذه السنة (This Year)', value: 'year' },
        { text: 'آخر 7 أيام', value: 'last_7_days' },
        { text: 'آخر 30 يوم', value: 'last_30_days' },
        { text: 'فترة مخصصة (Custom)', value: 'custom' },
        { text: 'الكل (All Time)', value: 'all' },
      ],

      summary: {
        total_driver_earnings: '0.00',
        drivers_count: 0,
        total_trips_operated: 0,
      },

      table: {
        data: [],
        total: 0,
        page: 1,
        per_page: 15,
        last_page: 1,
      },

      expanded: [],

      headers: [
        { text: 'رقم السائق', value: 'driver_id' },
        { text: 'اسم السائق', value: 'driver_name' },
        { text: 'رقم الهاتف', value: 'driver_phone' },
        { text: 'عدد الرحلات المنفذة', value: 'trips_count' },
        { text: 'إجمالي الأرباح المستحقة', value: 'total_earnings' },
        { text: '', value: 'data-table-expand' },
      ],
    };
  },
  watch: {
    value(val) {
      this.internalDialog = val;
      if (val) {
        this.fetchReport();
      }
    },
    internalDialog(val) {
      this.$emit('input', val);
    },
  },
  methods: {
    onClearSearch() {
      this.searchQuery = '';
      this.table.page = 1;
      this.fetchReport();
    },
    fetchReport() {
      this.loading = true;
      this.errorMessage = null;

      const params = {
        period: this.selectedPeriod,
        search: this.searchQuery,
        page: this.table.page,
        per_page: this.table.per_page,
      };

      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/driver-earnings', { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const d = res.data.data;
            this.summary = d.summary || this.summary;
            this.table = d.drivers_table || this.table;
          } else {
            this.errorMessage = res.data.message || 'خطأ في جلب التقرير';
          }
        })
        .catch((err) => {
          this.errorMessage = 'تعذر الاتصال بالخادم لجلب التقرير';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportCsv() {
      this.loadingCsv = true;
      const params = {
        period: this.selectedPeriod,
        search: this.searchQuery,
      };
      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/driver-earnings/export-csv', {
          params,
          responseType: 'blob',
        })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `driver-earnings-${moment().format('YYYY-MM-DD')}.csv`;
          link.click();
          window.URL.revokeObjectURL(link.href);
        })
        .catch((err) => {
          console.error('Error exporting CSV:', err);
        })
        .finally(() => {
          this.loadingCsv = false;
        });
    },
    closeModal() {
      this.internalDialog = false;
    },
  },
};
</script>
